import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useToast } from '@/shared/contexts/ToastContext';

/**
 * Confirm Reset Page - Prevents email prefetching issues
 * User clicks button to actually use the confirmation URL
 */
const ConfirmReset: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);

  const confirmationUrl = searchParams.get('confirmation_url');

  useEffect(() => {
    // Debug logging
    console.log('🔍 ConfirmReset - URL params:', Object.fromEntries(searchParams));
    console.log('🔍 ConfirmReset - confirmationUrl:', confirmationUrl);
    
    if (!confirmationUrl) {
      console.error('❌ ConfirmReset - No confirmation_url parameter found');
      showToast({
        type: 'error',
        message: 'Link reset tidak valid. Silakan minta link reset baru.'
      });
      setTimeout(() => navigate('/forgot-password'), 2000);
    } else {
      console.log('✅ ConfirmReset - Valid confirmation URL found');
    }
  }, [confirmationUrl, navigate, showToast]);

  const handleConfirmReset = () => {
    if (!confirmationUrl) {
      console.error('❌ ConfirmReset - No confirmationUrl to redirect to');
      return;
    }
    
    console.log('🚀 ConfirmReset - Redirecting to:', confirmationUrl);
    setIsProcessing(true);
    
    try {
      // Redirect to the actual confirmation URL
      window.location.href = confirmationUrl;
    } catch (error) {
      console.error('❌ ConfirmReset - Redirect failed:', error);
      showToast({
        type: 'error',
        message: 'Gagal melakukan redirect. Silakan coba lagi.'
      });
      setIsProcessing(false);
    }
  };

  if (!confirmationUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full">
          <div className="card p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Link Tidak Valid
            </h2>
            <p className="text-gray-600">
              Link reset password tidak valid atau sudah kadaluarsa.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Column - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-600 to-primary-700 p-12 flex-col justify-center items-center">
        <div className="max-w-lg text-center">
          {/* Logo */}
          <div className="flex items-center justify-center mb-8">
            <div className="bg-white rounded-full px-8 py-4 shadow-lg flex items-center gap-4">
              <img 
                src="/logo.png" 
                alt="Canvango Group" 
                className="h-12 w-auto"
              />
              <h1 className="text-2xl font-bold" style={{ color: '#5271ff' }}>CANVANGO GROUP</h1>
            </div>
          </div>
          
          {/* Heading */}
          <h2 className="text-white text-3xl font-bold mb-4 leading-tight">
            Konfirmasi Reset Password
          </h2>
          
          {/* Description */}
          <p className="text-primary-100 text-lg leading-relaxed">
            Klik tombol konfirmasi untuk melanjutkan proses reset password Anda dengan aman.
          </p>
        </div>
      </div>

      {/* Right Column - Confirmation */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Konfirmasi Reset Password
            </h2>
            <p className="text-gray-600">
              Klik tombol di bawah untuk melanjutkan reset password
            </p>
          </div>

          {/* Confirmation Card */}
          <div className="card p-8 text-center">
            {/* Success Icon */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-6">
              <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m0 0a2 2 0 01-2 2m2-2h.01M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>

            {/* Title */}
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Link Reset Password Valid
            </h3>

            {/* Message */}
            <p className="text-gray-600 mb-8">
              Kami telah memverifikasi permintaan reset password Anda. Klik tombol di bawah untuk melanjutkan ke halaman reset password.
            </p>

            {/* Confirm Button */}
            <button
              onClick={handleConfirmReset}
              disabled={isProcessing}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Memproses...
                </span>
              ) : (
                'Konfirmasi Reset Password'
              )}
            </button>

            {/* Security Note */}
            <p className="text-xs text-gray-500 mt-6 leading-relaxed">
              Langkah konfirmasi ini diperlukan untuk keamanan akun Anda dan mencegah akses yang tidak sah.
            </p>
          </div>

          {/* Back Link */}
          <div className="text-center mt-6">
            <button
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Minta Link Reset Baru
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmReset;